import {produce} from 'immer';
import queryClient from '@/utils/reactQueryClient';
import type {CreatorBrandSafetyVetting} from '@/types/creatorBrandSafetyVetting';
import {CreatorSafetyReport} from '@/types/creatorSafetyReport';
import {queryKey as creatorBrandSafetyVettingsQueryKey} from '@/hooks/queries/use-creator-brand-safety-vettings-query';
import {queryKey as vettingReportQueryKey} from '@/hooks/queries/use-vetting-report-query/useVettingReportQuery';

interface UpdateVettingReportTrackingParams {
  creatorId: string;
  reportId: string;
  tracking: boolean;
}

export const updateVettingReportTracking = ({
  creatorId,
  reportId,
  tracking,
}: UpdateVettingReportTrackingParams) => {
  const queryKey = [vettingReportQueryKey, creatorId, reportId];
  const previousData = queryClient.getQueryData(queryKey);

  queryClient.setQueryData(
    queryKey,
    (old: {data: CreatorSafetyReport} | undefined) => {
      if (!old) {
        return old;
      }
      return produce(old, (draft) => {
        if (draft) {
          draft.data.vettingTracking = tracking;
        }
      });
    }
  );
  return previousData;
};

interface UpdateVettingReportsTrackingParams {
  brandId: string;
  vettingId: string;
  tracking: boolean;
}

type InfiniteQueryData = {
  pages: {data: CreatorBrandSafetyVetting[]}[];
  pageParams: unknown[];
};

export const updateCreatorBrandSafetyVettingsTracking = ({
  brandId,
  vettingId,
  tracking,
}: UpdateVettingReportsTrackingParams) => {
  const queryKeyPrefix = [creatorBrandSafetyVettingsQueryKey, brandId];
  const matchingQueries = queryClient.getQueriesData<InfiniteQueryData>({
    queryKey: queryKeyPrefix,
  });

  const previousDataMap = new Map(matchingQueries);

  matchingQueries.forEach(([key, data]) => {
    if (!data?.pages) return;

    queryClient.setQueryData(
      key,
      produce(data, (draft) => {
        draft.pages.some((page) => {
          const vettingRow = page.data?.find(
            (vetting) => vetting.id === vettingId
          );
          if (vettingRow) {
            vettingRow.isLiveTracking = tracking;
            return true;
          }
          return false;
        });
      })
    );
  });

  return previousDataMap;
};
